import React, { useState, useEffect } from "react";
import {
	Modal,
	ModalHeader,
	ModalBody,
	ModalFooter,
	Button,
	Form,
	FormGroup,
	Label,
	Input,
	Alert,
	Spinner,
	Row,
	Col
} from "reactstrap";
import { useSelector, useDispatch } from "react-redux";
import { IApplicationState } from "../../redux/reducers";
import { IProduct } from "../../interfaces/products";
import { calculateScoreAPI } from "../../services/formulationService";
import productService from "../../services/productService";
import "../../assets/scss/modals/_score-calculation-modal.scss";

interface ScoreCalculationModalProps {
	isOpen: boolean;
	toggle: () => void;
	onScoreCalculated?: (data: any) => void;
}

const ScoreCalculationModal: React.FC<ScoreCalculationModalProps> = ({
	isOpen,
	toggle,
	onScoreCalculated
}) => {
	const dispatch = useDispatch();
	const { product } = useSelector((state: IApplicationState) => state);
	const { active, concept } = product;

	const [selectedProductId, setSelectedProductId] = useState<string>("");
	const [isCalculating, setIsCalculating] = useState(false);
	const [scoreResult, setScoreResult] = useState<any>(null);
	const [error, setError] = useState<string | null>(null);
	const [showResult, setShowResult] = useState(false);
	const [isLoadingProducts, setIsLoadingProducts] = useState(false);

	// Combine active and concept products for selection
	const allProducts = [...(active.list || []), ...(concept.list || [])];

	useEffect(() => {
		if (isOpen && allProducts.length === 0) {
			loadProducts();
		}
	}, [isOpen]);

	const loadProducts = async () => {
		setIsLoadingProducts(true);
		try {
			await dispatch(productService.getActiveProducts());
			await dispatch(productService.getConceptProducts());
		} catch (err) {
			console.error("Error loading products:", err);
		} finally {
			setIsLoadingProducts(false);
		}
	};

	const handleCalculate = async () => {
		if (!selectedProductId) {
			setError("Please select a product");
			return;
		}

		setIsCalculating(true);
		setError(null);
		setScoreResult(null);
		setShowResult(false);

		try {
			const result = await calculateScoreAPI(selectedProductId);

			if (!result.success) {
				throw new Error(result.error || "Failed to calculate scores");
			}

			setScoreResult(result.data);
			setShowResult(true);

			if (onScoreCalculated) {
				onScoreCalculated(result.data);
			}
		} catch (err) {
			console.error("Error calculating scores:", err);
			const errorMessage =
				err instanceof Error
					? err.message
					: "Failed to calculate scores. Please try again.";
			setError(errorMessage);
			setScoreResult(null);
			setShowResult(false);
		} finally {
			setIsCalculating(false);
		}
	};

	const handleClose = () => {
		setSelectedProductId("");
		setScoreResult(null);
		setError(null);
		setShowResult(false);
		toggle();
	};

	const handleNewCalculation = () => {
		setScoreResult(null);
		setShowResult(false);
		setError(null);
		setSelectedProductId("");
	};

	const selectedProduct = allProducts.find((p) => p.id === selectedProductId);

	const formatLabel = (key: string): string => {
		return key
			.split("_")
			.map((word) => word.charAt(0).toUpperCase() + word.slice(1))
			.join(" ");
	};

	const formatValue = (value: unknown, isCurrency: boolean = false): string => {
		if (typeof value === "number") {
			return isCurrency ? `$${value.toFixed(2)}` : value.toFixed(1);
		}
		if (typeof value === "string") {
			return value;
		}
		return "0.0";
	};

	const renderScoreBreakdown = (data: any) => {
		if (!data) return null;

		// Extract breakdown data
		const breakdown = data.breakdown || {};
		const nutritionBreakdown = breakdown.nutrition || {};
		const roiBreakdown = breakdown.roi || {};
		const sustainabilityBreakdown = breakdown.sustainability || {};

		return (
			<div className="score-breakdown">
				{/* Overall Scores Section */}
				<div className="score-section mb-4">
					<h5 className="score-section-title mb-3">Overall Scores</h5>
					<Row>
						<Col md={6} className="mb-3">
							<div className="score-item">
								<div className="score-label">Nutrition Score</div>
								<div className="score-value">
									{typeof data.nutrition_score === "number"
										? data.nutrition_score.toFixed(1)
										: data.nutrition_score || "0.0"}
								</div>
							</div>
						</Col>
						<Col md={6} className="mb-3">
							<div className="score-item">
								<div className="score-label">ROI Score</div>
								<div className="score-value">
									{typeof data.roi_score === "number"
										? data.roi_score.toFixed(1)
										: data.roi_score || "0.0"}
								</div>
							</div>
						</Col>
						<Col md={6} className="mb-3">
							<div className="score-item">
								<div className="score-label">Sustainability Score</div>
								<div className="score-value">
									{typeof data.sustainability_score === "number"
										? data.sustainability_score.toFixed(1)
										: data.sustainability_score || "0.0"}
								</div>
							</div>
						</Col>
						<Col md={6} className="mb-3">
							<div className="score-item overall-score">
								<div className="score-label">Overall Score</div>
								<div className="score-value">
									{typeof data.overall_score === "number"
										? data.overall_score.toFixed(1)
										: data.overall_score || "0.0"}
								</div>
							</div>
						</Col>
					</Row>
				</div>

				{/* Nutrition Breakdown Section */}
				{Object.keys(nutritionBreakdown).length > 0 && (
					<div className="score-section mb-4">
						<h5 className="score-section-title mb-3">Nutrition Breakdown</h5>
						<Row>
							{Object.entries(nutritionBreakdown).map(([key, value]) => (
								<Col key={key} md={6} className="mb-3">
									<div className="score-item">
										<div className="score-label">{formatLabel(key)}</div>
										<div className="score-value">
											{formatValue(value)}
										</div>
									</div>
								</Col>
							))}
						</Row>
					</div>
				)}

				{/* ROI Breakdown Section */}
				{Object.keys(roiBreakdown).length > 0 && (
					<div className="score-section mb-4">
						<h5 className="score-section-title mb-3">ROI Breakdown</h5>
						<Row>
							{Object.entries(roiBreakdown).map(([key, value]) => (
								<Col key={key} md={6} className="mb-3">
									<div className="score-item">
										<div className="score-label">{formatLabel(key)}</div>
										<div className="score-value">
											{formatValue(value, key === "cost_per_serving" || key === "market_price")}
										</div>
									</div>
								</Col>
							))}
						</Row>
					</div>
				)}

				{/* Sustainability Breakdown Section */}
				{Object.keys(sustainabilityBreakdown).length > 0 && (
					<div className="score-section mb-4">
						<h5 className="score-section-title mb-3">Sustainability Breakdown</h5>
						<Row>
							{Object.entries(sustainabilityBreakdown).map(([key, value]) => (
								<Col key={key} md={6} className="mb-3">
									<div className="score-item">
										<div className="score-label">{formatLabel(key)}</div>
										<div className="score-value">
											{formatValue(value)}
										</div>
									</div>
								</Col>
							))}
						</Row>
					</div>
				)}

				{/* Calculated At Timestamp */}
				{data.calculated_at && (
					<div className="score-timestamp mt-3 pt-3 border-top">
						<small className="text-muted">
							Calculated at: {new Date(data.calculated_at).toLocaleString()}
						</small>
					</div>
				)}
			</div>
		);
	};

	return (
		<Modal
			isOpen={isOpen}
			toggle={handleClose}
			size="lg"
			className="score-calculation-modal"
		>
			<ModalHeader toggle={handleClose}>Calculate Performance Scores</ModalHeader>
			<ModalBody>
				{!showResult ? (
					<div>
						<Form>
							<FormGroup>
								<Label for="productSelect">Select Product *</Label>
								{isLoadingProducts ? (
									<div className="text-center py-3">
										<Spinner size="sm" color="primary" />
										<p className="mt-2">Loading products...</p>
									</div>
								) : allProducts.length === 0 ? (
									<Alert color="warning">
										No products available. Please create a product first.
									</Alert>
								) : (
									<Input
										type="select"
										id="productSelect"
										value={selectedProductId}
										onChange={(e) => setSelectedProductId(e.target.value)}
										disabled={isCalculating}
									>
										<option value="">-- Select a product --</option>
										{allProducts.map((product) => (
											<option key={product.id} value={product.id}>
												{product.name} {product.flavor ? `- ${product.flavor}` : ""}
												{product.product_status ? " (Active)" : " (Concept)"}
											</option>
										))}
									</Input>
								)}
								{selectedProduct && (
									<div className="selected-product-info mt-2 p-2 bg-light rounded">
										<small>
											<strong>Product:</strong> {selectedProduct.name}
											<br />
											{selectedProduct.product_type?.title && (
												<>
													<strong>Type:</strong> {selectedProduct.product_type.title}
													<br />
												</>
											)}
											{selectedProduct.serving_size > 0 && (
												<>
													<strong>Serving Size:</strong> {selectedProduct.serving_size}
													{selectedProduct.unit_serving_size || "g"}
												</>
											)}
										</small>
									</div>
								)}
							</FormGroup>
						</Form>

						{error && (
							<Alert color="danger" className="mt-3">
								{error}
							</Alert>
						)}

						{isCalculating && (
							<div className="text-center mt-3">
								<Spinner color="primary" />
								<p className="mt-2">Calculating performance scores...</p>
							</div>
						)}
					</div>
				) : (
					<div>
						<Alert color="success" className="mb-3">
							<strong>Scores Calculated Successfully!</strong>
						</Alert>
						{selectedProduct && (
							<div className="selected-product-info mb-3 p-2 bg-light rounded">
								<small>
									<strong>Product:</strong> {selectedProduct.name}
								</small>
							</div>
						)}
						{scoreResult && renderScoreBreakdown(scoreResult)}
					</div>
				)}
			</ModalBody>
			<ModalFooter>
				{!showResult ? (
					<>
						<Button color="secondary" onClick={handleClose} disabled={isCalculating}>
							Cancel
						</Button>
						<Button
							color="primary"
							onClick={handleCalculate}
							disabled={isCalculating || !selectedProductId || isLoadingProducts}
						>
							{isCalculating ? (
								<>
									<Spinner size="sm" className="mr-2" />
									Calculating...
								</>
							) : (
								"Calculate Scores"
							)}
						</Button>
					</>
				) : (
					<>
						<Button color="secondary" onClick={handleNewCalculation}>
							Calculate Another
						</Button>
						<Button color="success" onClick={handleClose}>
							Close
						</Button>
					</>
				)}
			</ModalFooter>
		</Modal>
	);
};

export default ScoreCalculationModal;



